<template>
  <div class="sidebar-accordion">
    <div
      v-for="section in sections"
      :key="section.id"
      ref="sectionRefs"
      :data-section-id="section.id"
      :class="['sidebar-accordion__section', { 'is-open': isOpen(section.id), 'is-active': isSectionActive(section.id) }]"
    >
      <button
        type="button"
        class="sidebar-accordion__header"
        @click="toggleSection(section.id)"
      >
        <span class="sidebar-accordion__title">{{ section.title }}</span>
        <span class="sidebar-accordion__arrow"></span>
      </button>
      <transition
        name="sidebar-accordion__collapse"
        @before-enter="handleBeforeEnter"
        @enter="handleEnter"
        @after-enter="handleAfterEnter"
        @before-leave="handleBeforeLeave"
        @leave="handleLeave"
        @after-leave="handleAfterLeave"
      >
        <ul
          v-show="isOpen(section.id)"
          class="sidebar-accordion__items"
        >
          <li
            v-for="item in section.items"
            :key="item.id"
            :class="['sidebar-accordion__item', { 'is-active': isItemActive(section.id, item.id), 'has-arrow': hasArrow(item) }]"
          >
            <router-link
              v-if="item.route"
              :to="item.route"
              class="sidebar-accordion__item-link"
            >
              <span>{{ item.label }}</span>
              <span
                v-if="hasArrow(item)"
                class="sidebar-accordion__item-arrow"
                :style="arrowStyle(item)"
              ></span>
            </router-link>
            <div v-else class="sidebar-accordion__item-link">
              <span>{{ item.label }}</span>
              <span
                v-if="hasArrow(item)"
                class="sidebar-accordion__item-arrow"
                :style="arrowStyle(item)"
              ></span>
            </div>
          </li>
        </ul>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SidebarAccordion',
  props: {
    sections: {
      type: Array,
      default: () => []
    },
    activeSectionId: {
      type: String,
      default: ''
    },
    activeItemId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      openSections: {}
    };
  },
  watch: {
    sections: {
      immediate: true,
      handler() {
        this.initializeOpenSections();
      },
      deep: true
    },
    activeSectionId(newVal) {
      if (newVal) {
        this.$set(this.openSections, newVal, true);
      }
    }
  },
  methods: {
    initializeOpenSections() {
      const nextState = {};
      this.sections.forEach((section) => {
        const shouldOpen =
          typeof section.defaultOpen === 'boolean'
            ? section.defaultOpen
            : section.id === this.activeSectionId;
        nextState[section.id] = shouldOpen;
      });
      this.openSections = nextState;
    },
    isOpen(sectionId) {
      return !!this.openSections[sectionId];
    },
    toggleSection(sectionId) {
      const current = this.isOpen(sectionId);
      this.$set(this.openSections, sectionId, !current);
    },
    isSectionActive(sectionId) {
      return sectionId === this.activeSectionId;
    },
    isItemActive(sectionId, itemId) {
      return sectionId === this.activeSectionId && itemId === this.activeItemId;
    },
    hasArrow(item) {
      return item && !!item.arrow;
    },
    arrowStyle(item) {
      if (!item || !item.arrow) return {};
      if (item.arrow === 'primary') {
        return { borderLeftColor: '#e1601f' };
      }
      if (item.arrow === 'secondary') {
        return { borderLeftColor: '#bcbcbc' };
      }
      return {};
    },
    handleBeforeEnter(el) {
      el.style.height = '0';
      el.style.opacity = '0';
      el.style.transform = 'scaleY(0)';
    },
    handleEnter(el) {
      const height = el.scrollHeight;
      void el.offsetHeight;
      el.style.height = `${height}px`;
      el.style.opacity = '1';
      el.style.transform = 'scaleY(1)';
    },
    handleAfterEnter(el) {
      el.style.height = '';
      el.style.opacity = '';
      el.style.transform = '';
    },
    handleBeforeLeave(el) {
      el.style.height = `${el.scrollHeight}px`;
      el.style.opacity = '1';
      el.style.transform = 'scaleY(1)';
    },
    handleLeave(el) {
      void el.offsetHeight;
      el.style.height = '0';
      el.style.opacity = '0';
      el.style.transform = 'scaleY(0)';
    },
    handleAfterLeave(el) {
      el.style.height = '';
      el.style.opacity = '';
      el.style.transform = '';
    }
  }
};
</script>

<style scoped>
.sidebar-accordion {
  width: 274px;
  display: flex;
  flex-direction: column;
}

.sidebar-accordion__section + .sidebar-accordion__section {
  margin-top: 14px;
}

.sidebar-accordion__header {
  width: 100%;
  border: none;
  outline: none;
  padding: 16px 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  color: #000;
  transition: background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1), color 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: background-color, color;
}

.sidebar-accordion__section.is-active .sidebar-accordion__header {
  background-color: #e1601f;
  color: #fff;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.sidebar-accordion__header:hover {
  background-color: #e1601f;
  color: #fff;
}

.sidebar-accordion__title {
  font-family: SourceHanSerifCN-SemiBold, 'PingFang SC', sans-serif;
  font-size: 18px;
  line-height: 26px;
  font-weight: 600;
}

.sidebar-accordion__arrow {
  width: 10px;
  height: 10px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.sidebar-accordion__section.is-open .sidebar-accordion__arrow {
  transform: rotate(-135deg);
}

.sidebar-accordion__items {
  background-color: #fff;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.03);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  padding: 0;
  overflow: hidden;
  transform-origin: top;
  transform: scaleY(1);
  opacity: 1;
  transition:
    height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.45s ease;
  will-change: height, transform, opacity;
}

.sidebar-accordion__item {
  list-style: none;
}

.sidebar-accordion__item + .sidebar-accordion__item {
  border-top: 1px solid #e6e6e6;
}

.sidebar-accordion__item-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  padding: 4px 24px;
  font-family: PingFangSC-Semibold, 'PingFang SC', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #000;
  text-decoration: none;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  transition: color 0.25s ease;
}

.sidebar-accordion__item.is-active .sidebar-accordion__item-link {
  color: #e1601f;
}

@media (hover: hover) {
  .sidebar-accordion__item:hover .sidebar-accordion__item-link {
    color: #e1601f;
  }
}

.sidebar-accordion__item:last-child .sidebar-accordion__item-link span:first-child {
  margin-top: 10px;
}

.sidebar-accordion__item:last-child .sidebar-accordion__item-arrow {
  margin-top: 10px;
}

.sidebar-accordion__item:first-child .sidebar-accordion__item-link {
  padding-top: 16px;
}

.sidebar-accordion__item:last-child .sidebar-accordion__item-link {
  padding-bottom: 16px;
}

.sidebar-accordion__item-arrow {
  width: 0;
  height: 0;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 10px solid transparent;
}

</style>
